import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";

import { Helmet } from "react-helmet";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";

import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";

import Typography from "@mui/material/Typography";


import { RequireAuth } from "../../RequireAuth.jsx";

import { AuthContext } from "../../context/AuthContextProvider";

import PatientProfile from "./PatientProfile.jsx";
import PatientECGReports from "./PatientECGReports.jsx";
import PatientOtherReports from "./PatientOtherReports.jsx";
import PatientABHA from "./PatientABHA.jsx";

import {
  useFetchPatient,
  useFetchRegistry,
} from "../../api/collection";

import { useGetEcgList } from "../../api/EcgReports";

import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

const Settings = () => {
  const { id } = useParams();

  const [searchParams] = useSearchParams();

  // get tab from query
  const tabFromQuery =
    searchParams.get("tab");

  const fetchParams = useMemo(
    () => ({ id }),
    [id]
  );

  let {
    user: patient,
    isLoading,
  } = useFetchPatient(fetchParams);

  const { ecgReport: ecgReports = [] } =
    useGetEcgList({
      patientId: patient?.patientId,
    });

  const { report = [] } =
    useFetchRegistry({
      userId: patient?.id || patient?._id,
    });

  // active tab
  const [activeKey, setActiveKey] =
    useState(tabFromQuery || "profile");

  const { user } = useContext(AuthContext);

  const [reportToken, setReportToken] =
    useState(null);

  // counts
  const [counts, setCounts] = useState({
    localEcgCount: null,
    otherReportsCount: null,
  });

  const handleCountsChange = useCallback(
    (partial) => {
      setCounts((prev) => {
        const next = { ...prev };

        if (
          partial.localEcgCount !== undefined &&
          partial.localEcgCount !== null
        ) {
          next.localEcgCount =
            partial.localEcgCount;
        }

        if (
          partial.otherReportsCount !==
            undefined &&
          partial.otherReportsCount !== null
        ) {
          next.otherReportsCount =
            partial.otherReportsCount;
        }

        return next;
      });
    },
    []
  );

  const hasRole = (allowedRoles) => {
    return allowedRoles.includes(
      user?.userType
    );
  };

  const handleTabsChange = (
    event,
    value
  ) => {
    setActiveKey(value);
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Settings</title>
      </Helmet>

      <div className="main-wrappers">
        <div
          className="page-wrapper"
          style={{
            paddingBottom: "10px",
            paddingTop: "75px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <div className="content">
            {/* Breadcrumbs */}
            <Breadcrumbs
              aria-label="breadcrumb"
              sx={{ mb: 1 }}
            >
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/test-collections"
                sx={{
                  fontSize: "14px",
                  fontFamily: "Figtree",
                }}
              >
                Test Collections
              </Link>

              <Typography
                sx={{
                  fontSize: "14px !important",
                  fontWeight: "bold",
                  fontFamily: "Figtree",
                }}
              >
                {patient?.patientId}
              </Typography>
            </Breadcrumbs>

            {/* Header */}
            <div
              className="d-flex align-items-center mb-2"
              style={{ gap: "10px" }}
            >
              <Typography
                sx={{
                  fontSize: "20px !important",
                  fontWeight: "bold",
                  color: "var(--app-text)",
                }}
              >
                {patient?.patientId}

                {patient?.firstName ||
                patient?.lastName
                  ? " - "
                  : ""}

                {patient?.firstName || ""}{" "}
                {patient?.lastName || ""}
              </Typography>

              {patient?.isAbhaVerified && (
                <span
                  className="d-inline-flex align-items-center"
                  style={{
                    color: "#28a745",
                    backgroundColor:
                      "#d4edda",
                    padding: "2px 10px",
                    borderRadius: "5px",
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  ABHA Verified
                </span>
              )}
            </div>

            {/* Tabs */}
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "var(--app-border)",
              }}
            >
              <Tabs
                value={activeKey}
                onChange={handleTabsChange}
                variant="scrollable"
                scrollButtons={false}
                aria-label="settings tabs"
                textColor="inherit"
                indicatorColor="primary"
                TabIndicatorProps={{
                  sx: {
                    backgroundColor:
                      "#5751E9",
                    height: "3px",
                    borderRadius: "12px",
                  },
                }}
                sx={{
                  "& .MuiTabs-flexContainer":
                    {
                      gap: "82px",
                    },

                  "& .MuiTab-root": {
                    textTransform:
                      "none",

                    fontSize: "15px",

                    fontWeight: 500,

                    color: "#4B5563",

                    minWidth:
                      "auto !important",

                    paddingX: "0px",

                    fontFamily:
                      "Figtree",
                  },

                  "& .Mui-selected": {
                    color: "var(--app-text)",

                    fontWeight: 600,
                  },
                }}
              >
                <Tab
                  value="profile"
                  label="Patient Profile"
                />

                {hasRole([
                  "b2b",
                  "b2b-admin",
                ]) && (
                  <Tab
                    value="ecg_reports"
                    label={`ECG Reports ${
                      counts.localEcgCount !==
                      null
                        ? `(${counts?.localEcgCount})`
                        : `(${ecgReports?.length})`
                    }`}
                  />
                )}

                <Tab
                  value="other_reports"
                  label={`Other Reports ${
                    counts.otherReportsCount !==
                    null
                      ? `(${counts?.otherReportsCount})`
                      : `(${report?.length})`
                  }`}
                />

                {patient?.isAbhaVerified && (
                  <Tab
                    value="abha"
                    label="ABHA"
                  />
                )}
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ mt: 2 }}>
              {activeKey === "profile" && (
                <PatientProfile
                  patient={patient}
                />
              )}

              {activeKey ===
                "ecg_reports" &&
                hasRole([
                  "b2b",
                  "b2b-admin",
                ]) && (
                  <PatientECGReports
                    patient={patient}
                    setReportToken={
                      setReportToken
                    }
                    reportToken={
                      reportToken
                    }
                    onCountsChange={
                      handleCountsChange
                    }
                  />
                )}

              {activeKey ===
                "other_reports" && (
                  <PatientOtherReports
                    patient={patient}
                    onCountsChange={
                      handleCountsChange
                    }
                  />
                )}

              {activeKey === "abha" &&
                patient?.isAbhaVerified && (
                  <PatientABHA
                    patient={patient}
                  />
                )}
            </Box>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;  